/*
 * Jira Ticket:
 * Created Date: Mon, 4th Mar 2019, 15:23:29 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { Fragment, useState, useEffect } from 'react';
import { Admin, Resource } from 'react-admin';
import { createBrowserHistory as createHistory } from 'history';
import i18nProvider from './i18n';
import { CircularProgress } from '@material-ui/core';
import authProvider from 'AuthProvider';
import buildProvider from 'DataProvider';
import { PersistentOrganisationProvider } from './Components/Contexts';
import { setTabbedFilterFieldReducer } from 'reducers';
import customRoutes from 'routes';
import theme from './theme';
import { appStyles } from 'styles';
import { Login } from 'resources/auth';
import CustomLayout, { NotFound } from 'Layout';
import Dashboard from 'resources/Dashboard';
import Organisation from 'resources/Organisation';
import Project from 'resources/Project';
import Site from 'resources/Site';
import Subcontract from 'resources/Subcontractor';
import Tower from 'resources/Tower';
import User from 'resources/User';
import Employment from 'resources/Employment';
import Inspection from 'resources/Inspection';
import TowerType from 'resources/TowerType';
import Region from 'resources/Region';
import Advert from 'resources/Advert';
import Contract from './resources/Contract';
import Configuration from 'resources/Configuration';
import SubscriptionTier from 'resources/SubscriptionTier';
import Template from 'resources/Template';
import { isSuperAdmin } from 'utils/roles';

const history = createHistory();
const stage = process.env.AWS_BRANCH;
const commit = process.env.AWS_COMMIT_ID;

const App = () => {
  const classes = appStyles();
  const [dataProvider, setDataProvider] = useState(null);

  useEffect(() => {
    const initialiseApp = async () => {
      return buildProvider().then(dataProvider => {
        return setDataProvider(() => dataProvider);
      });
    };
    initialiseApp();
  }, []);

  if (!dataProvider) {
    return (
      <div className={classes.loading}>
        {'Loading...'}
        <CircularProgress size={60} className={classes.circularProgress} />
      </div>
    );
  }

  return (
    <Fragment>
      <PersistentOrganisationProvider>
        <Admin
          history={history}
          authProvider={authProvider}
          dataProvider={dataProvider}
          title="PASMA Admin"
          loginPage={Login}
          i18nProvider={i18nProvider}
          theme={theme}
          catchAll={NotFound}
          layout={CustomLayout}
          dashboard={Dashboard}
          customRoutes={customRoutes}
          customReducers={{
            organisationTabsFilters: setTabbedFilterFieldReducer,
          }}
        >
          {/* permissions prop will contain an array of permissions from the AuthProvider e.g. ["SUPER"] */}
          {(
            permissions // this permissions prop never updates so breaks when our user roles update
          ) => [
            <Resource name="Inspection" {...Inspection} />,
            <Resource name="Project" {...Project} />,
            <Resource name="Site" {...Site} />,
            <Resource name="Organisation" {...Organisation} />,
            <Resource name="TowerType" {...TowerType} />,
            <Resource name="User" {...User} />,
            <Resource name="Tower" {...Tower} />,
            <Resource name="Employment" {...Employment} />,
            <Resource name="Subcontractor" {...Subcontract} />,
            <Resource name="Contract" {...Contract} />,
            <Resource name="Template" {...Template} />,
            <Resource name="Advert" {...Advert} />,
            isSuperAdmin(permissions) ? <Resource name="Country" /> : null,
            isSuperAdmin(permissions) ? <Resource name="Locale" /> : null,
            <Resource name="Region" {...Region} />,
            <Resource name="SubscriptionTier" {...SubscriptionTier} />,
            <Resource name="Configuration" {...Configuration} />,
            <Resource name="OrganisationSearch" />,
          ]}
        </Admin>
      </PersistentOrganisationProvider>
      {/* footer to display branch and commit id in Amplify for test purposes */}
      {stage &&
      !(
        stage.startsWith('prod') ||
        stage === 'master' ||
        stage === 'client-staging'
      ) ? (
        <div className={classes.devCommit}>
          {stage} {commit.substring(0, 4)}
        </div>
      ) : null}
    </Fragment>
  );
};

export default App;
